import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useSafeAreaInsets } from "./useSafeAreaInsets";
import { deviceDetector } from "utils";

/**
 * 管理 Chat 消息列表最后一条消息气泡的 `minHeight`。
 *
 * ## 状态转换
 * ```
 * 初始化                                  → 0
 * captureBeforeSend()                     → window.innerHeight - 216
 * isLoading=true（AI 消息渲染进 DOM）       → window.innerHeight - 300
 * isLoading=false + 用户滚动              → 0
 * ```
 *
 * ## 已知问题
 * captureBeforeSend 时用 216，isLoading=true 时用 300，差值 84px，
 * 暂未找到确切原因（可能与 AI 消息首次渲染时的布局高度变化有关）；
 * 当前已确认 `scrollContainerRef` 不包含安全区，因此已移除安全区相关计算。
 *
 * ## Scroll 监听优化
 * 通过 `listenScrollRef` 门控：minHeight=0 且非 loading 时 scroll handler 立即返回，
 * 避免空闲期多余执行；captureBeforeSend 后开启，重置后自动关闭。
 *
 * @param isLoading          当前是否正在流式输出（来自 useChats）
 * @param scrollContainerRef 消息列表的滚动容器 ref
 */
export function useMessageMinHeight(
  isLoading: boolean | undefined,
  scrollContainerRef: RefObject<HTMLDivElement | null>,
  isModal?: boolean,
): { minHeight: number; captureBeforeSend: () => void } {
  const [minHeight, setMinHeight] = useState(0);
  const minHeightRef = useRef(0);
  const isLoadingRef = useRef(isLoading);
  // 用户物理交互标记：wheel/touchstart 后置 true，防止布局被动 scroll 误触发重置
  const userInteractedRef = useRef(false);
  // scroll 门控：captureBeforeSend → true；minHeight 重置 → false
  // 当 minHeight=0 且非 loading 时不需要处理 scroll，避免空闲期多余执行
  const listenScrollRef = useRef(false);
  const insets = useSafeAreaInsets();
  const isApp = deviceDetector.isApp();

  /**
   * 在用户发送消息时立即调用（早于 isLoading 异步更新）。
   * 立即撑开占位高度，使 AI 回复从顶部开始流式填充，消除发送后抖动。
   * 同时开启 scroll 门控，允许后续用户滚动收起占位。
   */
  const captureBeforeSend = useCallback(() => {
    userInteractedRef.current = false;
    listenScrollRef.current = true;
    // 330px = AI 消息问题初始高度 + 输入框等底部元素高度 + headers + 思考流占位
    const newMinHeight = isModal
      ? window.innerHeight - 360
      : window.innerHeight - 330 - insets.bottom - insets.top;
    minHeightRef.current = newMinHeight;
    setMinHeight(newMinHeight);
  }, [insets.bottom, insets.top, isModal]);

  // TODO: 非app的情况下，需要额外滚动设置min-height&滚动，否则滚动位置不对
  useLayoutEffect(() => {
    if (!isLoading || isApp) {
      return;
    }
    setMinHeight((pre) => pre + 90);
    const c = scrollContainerRef.current;
    if (!c) {
      return;
    }
    c.scrollTop = c.scrollHeight - c.clientHeight;
  }, [isLoading, scrollContainerRef, isModal, isApp]);

  // 同步 isLoadingRef，供 scroll handler 读取（避免闭包过期）
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // 监听用户物理交互 + scroll 重置 minHeight。
  // handleScroll 通过 listenScrollRef 门控：未激活时立即返回，避免多余执行；
  // 重置 minHeight 后关闭门控，后续 scroll 事件不再触发 setMinHeight。
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const handleUserInteract = () => {
      userInteractedRef.current = true;
    };

    const handleScroll = () => {
      if (!listenScrollRef.current) {
        return;
      }
      if (!isLoadingRef.current && userInteractedRef.current) {
        minHeightRef.current = 0;
        setMinHeight(0);
        listenScrollRef.current = false;
      }
    };

    container.addEventListener("wheel", handleUserInteract, { passive: true });
    container.addEventListener("touchstart", handleUserInteract, {
      passive: true,
    });
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("wheel", handleUserInteract);
      container.removeEventListener("touchstart", handleUserInteract);
      container.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef]);

  return { minHeight, captureBeforeSend };
}
